/**
 * Telemetria local de sincronização, persistida na object store
 * `syncBookkeeping` do IndexedDB (chave: `deviceId`) — CA-13.
 *
 * Renomeado de "device_sessions" (nome do rascunho da spec) para evitar
 * colisão com o conceito de "device session" do backend (sub_session/JWT,
 * HUB-21): isto é só telemetria local de sincronização, nunca autenticação.
 */
export interface SyncBookkeeping {
  readonly deviceId: string;
  readonly eventId: string;
  readonly lastSyncAt: string | null;
  readonly leadsSyncedInSession: number;
  /** Até 100 ids — são os `id` retornados pelo SERVIDOR (`Lead.id`), não o
   * `id` local de `QueuedLead`. */
  readonly lastSyncedLeadIds: readonly string[];
}

/** Máximo de ids retidos em `lastSyncedLeadIds` — CA-13: "ao ultrapassar
 * 100, os IDs mais antigos são descartados". */
const MAX_SYNCED_LEAD_IDS = 100;

/** Registro inicial (nenhuma sincronização ainda ocorreu neste dispositivo/
 * evento) — usado quando não existe registro prévio em `syncBookkeeping`. */
export function createEmptySyncBookkeeping(deviceId: string, eventId: string): SyncBookkeeping {
  return {
    deviceId,
    eventId,
    lastSyncAt: null,
    leadsSyncedInSession: 0,
    lastSyncedLeadIds: [],
  };
}

/**
 * Função pura que aplica um novo lead sincronizado ao bookkeeping existente
 * (CA-13): atualiza `lastSyncAt`, incrementa `leadsSyncedInSession` e anexa
 * o id do lead recém-sincronizado a `lastSyncedLeadIds`, mantendo só os 100
 * mais recentes (`Array.slice(-100)`) sem lançar erro ao ultrapassar o
 * limite.
 */
export function recordSyncedLead(
  current: SyncBookkeeping,
  serverLeadId: string,
  syncedAt: string
): SyncBookkeeping {
  return {
    ...current,
    lastSyncAt: syncedAt,
    leadsSyncedInSession: current.leadsSyncedInSession + 1,
    lastSyncedLeadIds: [...current.lastSyncedLeadIds, serverLeadId].slice(-MAX_SYNCED_LEAD_IDS),
  };
}
